import { useEffect, useCallback, useContext, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Socket } from 'socket.io-client';
import { FileSocketEvent, SocketNamespace } from '@trinity/chat';
import { useGetSocket } from '@trinity/chat-components/websocket/hooks/useGetSocket.js';
import { TagGeneratingFilesContext } from '@trinity/chat-components/containers/Workspace/context.js';

type TagsAvailablePayload = {
  fileId: number;
  updatedOn: Date;
};

function useFileSocket(): Socket | undefined {
  const [socket, setSocket] = useState<Socket>();
  const getSocket = useGetSocket(SocketNamespace.Files);

  useEffect(() => {
    const newSocket = getSocket();
    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [getSocket]);

  return socket;
}

export function useFileTagsEvents(fileIds: number[]) {
  const socket = useFileSocket();
  const queryClient = useQueryClient();
  const tagGeneratingContext = useContext(TagGeneratingFilesContext);

  const fileIdsKey = useMemo(() => {
    if (fileIds.length === 0) return '';
    return fileIds.slice().sort((a, b) => a - b).join(',');
  }, [fileIds]);

  const handleTagsAvailable = useCallback((payload: TagsAvailablePayload) => {
    try {
      const { fileId } = payload;

      queryClient.invalidateQueries({
        queryKey: ['get:workspaces/files', fileId],
      });
      queryClient.invalidateQueries({
        queryKey: ['get:workspaces/objects'],
      });

      queryClient.refetchQueries({
        queryKey: ['get:workspaces/files', fileId],
      });
      queryClient.refetchQueries({
        queryKey: ['get:workspaces/objects'],
      });
    } catch (error) {
    }
  }, [queryClient]);

  useEffect(() => {
    if (!socket || fileIdsKey === '') return;

    // Parse fileIds from key for subscription
    const currentFileIds = fileIdsKey.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id));

    // Subscribe to each file
    currentFileIds.forEach(fileId => {
      socket.emit('subscribe', fileId);
    });

    // Listen for TagsAvailable events
    socket.on(FileSocketEvent.TagsAvailable, handleTagsAvailable);

    // Cleanup: unsubscribe from files and remove listener
    return () => {
      currentFileIds.forEach(fileId => {
        socket.emit('unsubscribe', fileId);
      });
      socket.off(FileSocketEvent.TagsAvailable, handleTagsAvailable);
    };
  }, [socket, fileIdsKey, handleTagsAvailable]);
}

